import type { FC, ReactNode } from 'react'
import type { PhotoId } from '@/components/generals/photo'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { delay, vars } from '@/lib/motion'
import { Photo } from '@/components/generals/photo'
import { routes } from '@/constants/site'

/* Hero orbit: small round scout portraits drifting around the headline (desktop only) ---- */

interface RingScout {
    photo: PhotoId
    name: string
    className: string
    size: string
}

const RING: RingScout[] = [
    { photo: 'elena', name: 'Maren', className: 'left-[8%] top-[24%]', size: 'size-16' },
    { photo: 'luca', name: 'Teodor', className: 'left-[17%] top-[56%]', size: 'size-[52px]' },
    { photo: 'nora', name: 'Ines', className: 'left-[27%] top-[12%]', size: 'size-12' },
    { photo: 'sofia', name: 'Priya', className: 'right-[9%] top-[20%]', size: 'size-[60px]' },
    { photo: 'mateo', name: 'Tomás', className: 'right-[17%] top-[54%]', size: 'size-[52px]' },
    { photo: 'adrian', name: 'Felix', className: 'right-[27%] top-[11%]', size: 'size-12' }
]

export const ScoutRing: FC = (): ReactNode => {
    return (
        <div aria-hidden='true' className='pointer-events-none absolute inset-0 z-[1] hidden min-[992px]:block'>
            {RING.map((scout, index) => (
                <figure
                    key={scout.name}
                    className={cn('enter-fade absolute', scout.className)}
                    style={delay(500 + index * 90)}
                >
                    <div
                        className='drift flex flex-col items-center gap-[7px]'
                        style={vars({
                            '--drift-duration': `${16 + (index % 4) * 3}s`,
                            '--drift-y': `${index % 2 === 0 ? -10 : 9}px`,
                            '--drift-x': `${index % 3 === 0 ? 6 : -5}px`,
                            '--drift-delay': `${-index * 2.3}s`
                        })}
                    >
                        <Photo id={scout.photo} sizes='64px' portrait className={cn('rounded-full saturate-[0.85] sepia-[0.22] shadow-[0_0_0_3px_#fffdf8,0_6px_14px_-6px_rgb(40_38_34/0.45)]', scout.size)} />
                        <figcaption className='whitespace-nowrap text-[9px] font-medium uppercase tracking-[0.08em] text-ink/60'>
                            {scout.name}
                        </figcaption>
                    </div>
                </figure>
            ))}
        </div>
    )
}

/* Some people just get it ----------------------------------------------------------- */

interface Seat {
    photo: PhotoId
    name: string
    descriptor: string
}

const SEATS: Seat[] = [
    { photo: 'elena', name: 'Maren Holt', descriptor: 'Hosts a weekly design podcast' },
    { photo: 'nora', name: 'Ines Calloway', descriptor: 'Creative director to fast-moving startups' },
    { photo: 'luca', name: 'Teodor Valk', descriptor: 'Co-host of a design show and founder of a media studio' },
    { photo: 'sofia', name: 'Priya Nandakumar', descriptor: 'Runs a newsletter read by 250k designers' }
]

const SEAT_TURN = ['-2deg', '1.5deg', '-1deg', '2.2deg']

/** A seat: a frayed square of card with the scout's photo print pasted on it at a slight angle. */
const SEAT = 'relative block aspect-square w-[min(100%,220px)]'

const SeatPaper: FC = (): ReactNode => {
    return <span aria-hidden='true' className='fray absolute inset-[7px] rounded-[2px] bg-card' />
}

const CAPTION_TITLE = 'font-serif text-[16px] font-medium tracking-[-0.01em] text-ink'
const CAPTION_LINE = 'mx-auto mt-1 max-w-[24ch] text-[13px] leading-[19px] text-ink/[0.72]'

export const SomePeopleGetIt: FC = (): ReactNode => {
    return (
        <section
            id='some-people-get-it'
            aria-labelledby='get-it-title'
            className='section-x mx-auto max-w-[calc(1080px+2*clamp(16px,3vw,32px))] py-[90px] max-sm:pb-14 max-sm:pt-[60px]'
        >
            <div className='reveal'>
                <h2 id='get-it-title' className='type-section'>
                    Some people just get it
                </h2>
                <p className='mt-6 max-w-[44ch] text-ink/80'>
                    You know the designers doing great work, the ones privately considering their next move. Scouting lets you put the right opportunities in front of them
                </p>
            </div>

            <ul className='mt-[clamp(44px,5vw,72px)] grid grid-cols-5 gap-[clamp(14px,2vw,24px)] max-md:grid-cols-2 max-md:gap-x-[18px] max-md:gap-y-7'>
                {SEATS.map((seat, index) => (
                    <li key={seat.name} className='reveal flex flex-col items-center gap-4' style={delay(index * 100)}>
                        <span className={SEAT}>
                            <SeatPaper />
                            <span className='absolute inset-[15px] z-[1] block rounded-[3px] bg-paper p-1 shadow-[0_1px_2px_rgb(40_38_34/0.12),0_12px_24px_-14px_rgb(40_38_34/0.45)]' style={{ rotate: SEAT_TURN[index] }}>
                                <Photo id={seat.photo} sizes='(min-width: 768px) 200px, 45vw' className='size-full rounded-[2px]' />
                            </span>
                        </span>

                        <div className='text-center'>
                            <h3 className={CAPTION_TITLE}>
                                {seat.name}
                            </h3>
                            <p className={CAPTION_LINE}>
                                {seat.descriptor}
                            </p>
                        </div>
                    </li>
                ))}

                <li className='reveal flex flex-col items-center gap-4 max-md:col-span-2' style={delay(SEATS.length * 100)}>
                    <Link
                        href={routes.applyToScout}
                        aria-label='Apply to become a scout'
                        className={cn(SEAT, 'group rounded-[2px] max-md:aspect-auto max-md:h-24 max-md:w-full')}
                    >
                        <SeatPaper />
                        <i aria-hidden='true' className='hand absolute inset-0 z-[1] flex items-center justify-center text-[clamp(19px,1.7vw,25px)] not-italic leading-none text-ink/55 transition-colors duration-200 [rotate:-4deg] group-hover:text-ink/80'>
                            you?
                        </i>
                    </Link>

                    <div className='text-center'>
                        <h3 className={CAPTION_TITLE}>
                            Your name?
                        </h3>
                        <p className={CAPTION_LINE}>
                            The next seat is open. Join our group of scouts
                        </p>
                    </div>
                </li>
            </ul>
        </section>
    )
}
